import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../services/api'

export type WidgetId = 
  | 'shortcuts'
  | 'maturity'
  | 'market-ticker'
  | 'financial-intelligence'
  | 'kpi-cards'
  | 'cash-flow'
  | 'expense-chart'
  | 'fixed-variable-chart'
  | 'trend-chart'
  | 'fiscal-deadlines'
  | 'insights'

export interface Widget {
  id: WidgetId
  name: string
  nameEn: string
  visible: boolean
  order: number
}

const DEFAULT_WIDGETS: Widget[] = [
  { id: 'shortcuts', name: 'Scorciatoie Rapide', nameEn: 'Quick Shortcuts', visible: true, order: 0 },
  { id: 'market-ticker', name: 'Ticker Mercati', nameEn: 'Market Ticker', visible: false, order: 1 },
  { id: 'financial-intelligence', name: 'Financial Intelligence', nameEn: 'Financial Intelligence', visible: true, order: 2 },
  { id: 'kpi-cards', name: 'Statistiche Rapide', nameEn: 'Quick Stats', visible: true, order: 3 },
  { id: 'cash-flow', name: 'Flusso di Cassa', nameEn: 'Cash Flow', visible: true, order: 4 },
  { id: 'expense-chart', name: 'Distribuzione Spese', nameEn: 'Expense Distribution', visible: true, order: 5 },
  { id: 'fixed-variable-chart', name: 'Costi Fissi vs Variabili', nameEn: 'Fixed vs Variable', visible: true, order: 6 },
  { id: 'trend-chart', name: 'Trend Mensile', nameEn: 'Monthly Trend', visible: true, order: 7 },
  { id: 'maturity', name: 'Affidabilità Previsioni', nameEn: 'Prediction Reliability', visible: false, order: 8 },
  { id: 'fiscal-deadlines', name: 'Scadenze Fiscali', nameEn: 'Fiscal Deadlines', visible: false, order: 9 },
  { id: 'insights', name: 'Insights AI', nameEn: 'AI Insights', visible: true, order: 10 },
]

const STORAGE_KEY = 'dashboard-widgets-config'

function mergeWithDefaults(saved: Array<{ id: string; visible: boolean; order: number }>): Widget[] {
  return DEFAULT_WIDGETS.map(defaultWidget => {
    const savedWidget = saved.find(w => w.id === defaultWidget.id)
    if (savedWidget) {
      return { ...defaultWidget, visible: savedWidget.visible, order: savedWidget.order }
    }
    return defaultWidget
  }).sort((a, b) => a.order - b.order)
}

export function useDashboardWidgets(dashboardId?: string) {
  const queryClient = useQueryClient()

  // Fetch remote config when dashboardId is provided
  const { data: remoteConfig } = useQuery<{ widget_config: Array<{ id: string; visible: boolean; order: number }> }>({
    queryKey: ['dashboard-config', dashboardId],
    queryFn: () => api.get(`/dashboards/${dashboardId}`).then(r => r.data),
    enabled: !!dashboardId,
    staleTime: 60_000,
  })

  // Save remote config mutation
  const saveMutation = useMutation({
    mutationFn: (newConfig: Widget[]) =>
      api.put(`/dashboards/${dashboardId}`, {
        widget_config: newConfig.map(w => ({ id: w.id, visible: w.visible, order: w.order })),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dashboard-config', dashboardId] }),
  })

  const [widgets, setWidgets] = useState<Widget[]>(() => {
    if (!dashboardId) {
      try {
        const saved = localStorage.getItem(STORAGE_KEY)
        if (saved) {
          return mergeWithDefaults(JSON.parse(saved))
        }
      } catch { /* ignore */ }
    }
    return DEFAULT_WIDGETS
  })

  const [isConfiguring, setIsConfiguring] = useState(false)
  const prevDashboardIdRef = useRef(dashboardId)

  // Reset widgets when dashboardId changes (switch between dashboards)
  useEffect(() => {
    if (prevDashboardIdRef.current !== dashboardId) {
      prevDashboardIdRef.current = dashboardId
      if (!dashboardId) {
        // Going back to default: load from localStorage
        try {
          const saved = localStorage.getItem(STORAGE_KEY)
          setWidgets(saved ? mergeWithDefaults(JSON.parse(saved)) : DEFAULT_WIDGETS)
        } catch { setWidgets(DEFAULT_WIDGETS) }
      } else {
        // Switching to another custom dashboard: reset while loading
        setWidgets(DEFAULT_WIDGETS)
      }
    }
  }, [dashboardId])

  // Load remote config when available
  useEffect(() => {
    if (dashboardId && remoteConfig?.widget_config?.length) {
      setWidgets(mergeWithDefaults(remoteConfig.widget_config))
    }
  }, [dashboardId, remoteConfig])

  // Persist changes
  useEffect(() => {
    if (dashboardId) {
      // Debounce saves to API
      const timer = setTimeout(() => {
        if (remoteConfig) { // only save after initial load
          saveMutation.mutate(widgets)
        }
      }, 800)
      return () => clearTimeout(timer)
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(widgets))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgets, dashboardId])

  const toggleWidget = useCallback((id: WidgetId) => {
    setWidgets(prev => 
      prev.map(w => w.id === id ? { ...w, visible: !w.visible } : w)
    )
  }, [])

  const moveWidget = useCallback((id: WidgetId, direction: 'up' | 'down') => {
    setWidgets(prev => {
      const index = prev.findIndex(w => w.id === id)
      if (index === -1) return prev
      
      const newIndex = direction === 'up' ? index - 1 : index + 1
      if (newIndex < 0 || newIndex >= prev.length) return prev
      
      const newWidgets = [...prev]
      const [removed] = newWidgets.splice(index, 1)
      newWidgets.splice(newIndex, 0, removed)
      
      return newWidgets.map((w, i) => ({ ...w, order: i }))
    })
  }, [])

  const resetWidgets = useCallback(() => {
    setWidgets(DEFAULT_WIDGETS)
  }, [])

  const isWidgetVisible = useCallback((id: WidgetId) => {
    return widgets.find(w => w.id === id)?.visible ?? true
  }, [widgets])

  const getWidgetOrder = useCallback((id: WidgetId) => {
    return widgets.find(w => w.id === id)?.order ?? 0
  }, [widgets])

  return {
    widgets,
    isConfiguring,
    setIsConfiguring,
    toggleWidget,
    moveWidget,
    resetWidgets,
    isWidgetVisible,
    getWidgetOrder,
  }
}
